import { Injectable, OnDestroy } from '@angular/core';
import { cloneDeep } from '@apollo/client/utilities';
import { BrandLibraryDataService } from '@app/shared/media-library/brand-library.data.service';
import { isImageNode, isVideoNode, isWidgetNode } from '@creative/nodes/helpers';
import {
    AssetReference,
    IElementDataNode,
    IFeed,
    IImageElementAsset,
    IImageElementDataNode,
    IVideoElementAsset,
    IVideoElementDataNode,
    OneOfElementDataNodes
} from '@studio/domain';
import { IDesign, IElementProperty } from '@studio/domain/creativeset';
import { IBrandLibraryElement } from '@studio/domain/creativeset/brand-library';
import { createElement, createElementProperty } from '@studio/utils/element.utils';
import { getAssetReferenceTypeOfElement, getFeedReferenceTypeOfElement } from '@studio/utils/asset';
import { Subject } from 'rxjs';
import { DesignViewComponent } from '../design-view.component';
import { BrandLibraryElementService } from '../media-library';
import { EditorStateService } from './editor-state.service';
import { ElementSelectionService } from './element-selection.service';
import {
    EventLoggerService,
    ImagePropertyChangeEvent,
    VideoPropertyChangeEvent
} from '@studio/monitoring/browser/events';
import { MutatorService } from './mutator.service';
import { Logger } from '@studio/utils/logger';
import { codePropertyRegex } from '@creative/elements/widget/utils';
import { distinctArrayById } from '@studio/utils';

@Injectable()
export class ElementReplaceService implements OnDestroy {
    private designs: IDesign[] = [];
    private unsubscribe$ = new Subject<void>();

    private logger = new Logger('ElementReplaceService');

    constructor(
        private editorState: EditorStateService,
        private brandLibraryElementService: BrandLibraryElementService,
        private editor: DesignViewComponent,
        private selectionService: ElementSelectionService,
        private brandLibraryDataService: BrandLibraryDataService,
        private eventLogger: EventLoggerService,
        private mutator: MutatorService
    ) {
        this.designs = distinctArrayById([this.editorState.designFork, ...this.editorState.designs]);
    }

    replaceImage(imageAsset: IImageElementAsset, replaceInAllDesigns: boolean = false): void {
        const imageElements = this.selectionService.currentSelection.elements.filter(isImageNode);
        for (const element of imageElements) {
            this.eventLogger.log(
                new ImagePropertyChangeEvent('imageAsset', element.imageAsset?.url, imageAsset.url),
                this.logger
            );

            this.mutator.setElementPropertyValue(element, 'imageAsset', imageAsset);

            // replace in design
            if (replaceInAllDesigns) {
                this.replaceImageInAllDesigns(element, imageAsset);
            } else {
                this.replaceImageInDesign(element, imageAsset, this.editorState.designFork);
            }

            this.replaceElementWithMediaReference(element, imageAsset.id);
        }
    }

    replaceVideo(videoAsset: IVideoElementAsset, replaceInAllDesigns: boolean = false): void {
        const videoElements = this.selectionService.currentSelection.elements.filter(isVideoNode);
        for (const element of videoElements) {
            this.eventLogger.log(
                new VideoPropertyChangeEvent('videoAsset', element.videoAsset?.url, videoAsset.url),
                this.logger
            );

            this.mutator.setElementPropertyValue(element, 'videoAsset', videoAsset);

            if (replaceInAllDesigns) {
                this.replaceVideoInAllDesigns(element, videoAsset);
            } else {
                this.replaceVideoInDesign(element, videoAsset, this.editorState.designFork);
            }

            this.replaceElementWithMediaReference(element, videoAsset.id);
        }
    }

    replaceImageInAllDesigns(element: IImageElementDataNode, imageAsset: IImageElementAsset): void {
        this.designs.forEach(design => {
            this.replaceImageInDesign(element, imageAsset, design);
        });
    }

    replaceImageInDesign(
        element: IImageElementDataNode,
        imageAsset: IImageElementAsset,
        designFork: IDesign
    ): void {
        const newImageAsset: IImageElementAsset = {
            id: imageAsset.id,
            url: imageAsset.url,
            width: imageAsset.width,
            height: imageAsset.height
        };

        const elementInDesign = designFork.elements.find(e => e.id === element.id);
        if (elementInDesign) {
            // holding the image reference in the shared element properties list
            // is only used for BE to knwo what images being used in the creative set
            const property = elementInDesign.properties.find(e => e.name === AssetReference.Image);
            if (property) {
                property.value = newImageAsset.id;
            }

            const libraryElement = this.brandLibraryDataService.getElementByAssetId(
                AssetReference.Image,
                imageAsset.id
            );

            const documentElement = designFork.document.elements.find(
                e => e.id === elementInDesign.id
            ) as IImageElementDataNode;

            documentElement.feed = undefined;
            documentElement.imageAsset = newImageAsset;
            documentElement.imageSettings = cloneDeep(element.imageSettings); // also set new settings when switching to svg
            documentElement.parentId = libraryElement?.id;
        }
    }

    replaceVideoInAllDesigns(element: IVideoElementDataNode, videoAsset: IVideoElementAsset): void {
        this.designs.forEach(design => {
            this.replaceVideoInDesign(element, videoAsset, design);
        });
    }

    replaceVideoInDesign(
        element: IVideoElementDataNode,
        videoAsset: IVideoElementAsset,
        designFork: IDesign
    ): void {
        const newVideoAsset: IVideoElementAsset = {
            id: videoAsset.id,
            url: videoAsset.url,
            width: videoAsset.width,
            height: videoAsset.height,
            name: videoAsset.name,
            fileSize: videoAsset.fileSize
        };

        const elementInDesign = designFork.elements.find(e => e.id === element.id);
        if (elementInDesign) {
            const property = elementInDesign.properties.find(e => e.name === AssetReference.Video);
            if (property) {
                property.value = newVideoAsset.id;
            }

            const libraryElement = this.brandLibraryDataService.getElementByAssetId(
                AssetReference.Video,
                newVideoAsset.id
            );

            const documentElements = designFork.document.elements;
            const videoElement = documentElements.find(
                e => e.id === elementInDesign.id
            ) as IVideoElementDataNode;

            videoElement.feed = undefined;
            videoElement.videoAsset = newVideoAsset;
            videoElement.videoSettings = cloneDeep(element.videoSettings); // also set new settings when switching to svg
            videoElement.parentId = libraryElement?.id;
        }
    }

    replaceElementWithMediaReference(element: OneOfElementDataNodes, assetId: string): void {
        const editorElement = this.editorState.elements.find(el => el.id === element.id);

        if (!editorElement) {
            return;
        }

        const assetReference = getAssetReferenceTypeOfElement(editorElement);
        const feededReferenceName = getFeedReferenceTypeOfElement(editorElement);

        const currentAssetReference = editorElement.properties.find(
            prop => prop.name === assetReference
        );

        const feededReference = editorElement.properties.find(
            prop => prop.name === feededReferenceName
        );

        const brandElement = this.brandLibraryDataService.getElementByAssetId(assetReference, assetId);

        const referenceProperty = createElementProperty({
            ...currentAssetReference,
            name: assetReference,
            unit: 'id',
            value: assetId
        });

        if (brandElement) {
            const newElement = createElement({
                id: editorElement.id,
                name: editorElement.name,
                parentId: brandElement.id,
                type: editorElement.type,
                properties: []
            });

            if (feededReference) {
                newElement.properties.push(feededReference);
            } else {
                newElement.properties.push(referenceProperty);
            }

            const newElements = this.editorState.elements.filter(
                globalElement => globalElement.id !== editorElement.id
            );

            this.editorState.updateElements([...newElements, newElement]);
            return;
        }

        if (currentAssetReference) {
            currentAssetReference.value = assetId;
        } else {
            editorElement.properties.push(referenceProperty);
        }

        if (feededReference) {
            editorElement.properties = editorElement.properties.filter(
                prop => prop.name !== feededReferenceName
            );
        }
    }

    replaceFeededMediaInAllDesigns(element: IElementDataNode, feed: IFeed): void {
        this.designs.forEach(design => {
            this.replaceFeededMediaInDesign(element, feed, design);
        });
    }

    replaceFeededMediaInDesign(element: IElementDataNode, feed: IFeed, designFork: IDesign): void {
        const elementInDesign = designFork.elements.find(e => e.id === element.id);

        if (!elementInDesign) {
            return;
        }

        const documentElement = designFork.document.elements.find(e => e.id === elementInDesign.id)!;
        documentElement.feed = cloneDeep(feed);
        documentElement.parentId = undefined;

        if (isVideoNode(documentElement)) {
            documentElement.videoAsset = undefined;
        }

        if (isImageNode(documentElement)) {
            documentElement.imageAsset = undefined;
        }
    }

    updateWidgetInAllDesigns(widgetLibraryElement: IBrandLibraryElement): void {
        const designs = this.designs;

        const dataElements = this.editorState.document.elements;
        const designDocuments = designs.map(({ document }) => document);
        const globalDataElements = designDocuments.flatMap(({ elements }) => elements);
        // Get parentId on document elements since it's more reliable
        const widgetDataElements = [...dataElements, ...globalDataElements].filter(
            ({ parentId }) => parentId === widgetLibraryElement.id
        );

        const editorElements = this.editorState.elements;
        const globalElements = designs.flatMap(({ elements }) => elements);
        const elements = [...editorElements, ...globalElements].filter(
            element => !!widgetDataElements.find(({ id }) => id === element.id)
        );

        elements.forEach(element => {
            // Update code properties for each element
            const codeProperties = widgetLibraryElement.properties.filter(prop =>
                codePropertyRegex.test(prop.name)
            );
            codeProperties.forEach(prop => {
                const elementProperty = element.properties.find(({ name }) => name === prop.name);
                if (elementProperty) {
                    elementProperty.value = prop.value;
                } else {
                    element.properties.push({
                        unit: prop.unit,
                        label: prop.label,
                        value: prop.value,
                        name: prop.name
                    } as IElementProperty);
                }
            });

            const allWidgetElements = widgetDataElements.filter(({ id }) => id === element.id);

            // Update custom properties for all document elements with the element reference
            allWidgetElements.forEach(elementInDesign => {
                if (!isWidgetNode(elementInDesign)) {
                    return;
                }

                this.brandLibraryElementService.updateWidgetFromLibrary(
                    elementInDesign,
                    widgetLibraryElement,
                    element
                );
            });
        });

        /**
         * Reflect changes made to current elements on the global elements.
         * This occurs after doing the actual updating in order to prevent properties
         * to be created when they shouldn't
         */
        for (const element of elements) {
            for (const globalElement of globalElements) {
                if (element.id === globalElement.id) {
                    globalElement.properties = cloneDeep(element.properties);
                }
            }
        }

        const selectedNodes = this.selectionService.currentSelection.nodes;

        this.editor.rerenderCanvas();

        if (selectedNodes.length > 0) {
            this.selectionService.setSelection(...selectedNodes);
            this.editor.workspace.redrawGizmos();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
